/**
 * useClientLinkList (KAN-666): links of a client card as the UI shows them,
 * with the owner's decision on a link (KAN-667: "Отменить связь" = reject).
 *
 * Shared by the transcript modal header (TranscriptClientInfo) and the
 * "Связанные карточки" card on the client page, so both filter and decide the
 * same way: rejected links are hidden, a decision refreshes ['client-links']
 * for every open view of the links (the mutation of clientQueries does that).
 */
import { useCallback, useMemo } from 'react';
import {
  useClientLinks,
  useDecideClientLink,
} from '@/services/api/clientQueries';
import type {
  ClientLink,
  ClientLinkDecision,
  LinkedClientBrief,
} from '@/types/client';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { ANY_UUID } from '@/lib/managerAccess';

/** Display name of a linked card: name, else phone, else e-mail. */
export function linkedClientName(client: LinkedClientBrief): string {
  return client.name || client.phone || client.email || 'Без имени';
}

/** ", <role> (<company>)" after the name; "" when both are unknown. */
export function linkedClientDetails(client: LinkedClientBrief): string {
  return (
    (client.role ? `, ${client.role}` : '') +
    (client.company ? ` (${client.company})` : '')
  );
}

/** KAN-667: the link was confirmed automatically (no owner decision). */
export function isAutoConfirmed(link: ClientLink): boolean {
  return link.evidence?.auto_confirmed === true;
}

export interface ClientLinkListState {
  /** Confirmed (and legacy suggested) links; rejected ones are dropped. */
  links: ClientLink[];
  /** First load in progress (nothing to show yet). */
  loading: boolean;
  /** The list could not be loaded. */
  error: boolean;
  /** Cancel a link ("reject"); "confirm" is kept for compatibility. */
  decide: (linkId: string, decision: ClientLinkDecision) => void;
  /** Id of the link whose decision is being saved, or null. */
  decidingLinkId: string | null;
  /** KAN-811: the user may cancel links (closed to the MANAGER role). */
  canDecide: boolean;
}

export function useClientLinkList(
  clientId: string | null,
  enabled = true
): ClientLinkListState {
  const query = useClientLinks(clientId, enabled);
  const decision = useDecideClientLink(clientId);
  const canDecide = useCanCallApi('POST', `/api/v1/client-links/${ANY_UUID}/decide/`);

  const links = useMemo(
    () => (query.data ?? []).filter((link) => link.status !== 'rejected'),
    [query.data]
  );

  const { mutate } = decision;
  const decide = useCallback(
    (linkId: string, value: ClientLinkDecision) => {
      mutate({ linkId, decision: value });
    },
    [mutate]
  );

  return {
    links,
    loading: query.isLoading,
    error: query.isError,
    decide,
    decidingLinkId: decision.isPending
      ? (decision.variables?.linkId ?? null)
      : null,
    canDecide,
  };
}
